import { useState, useEffect } from "react";
import { 
  FaEdit, 
  FaTrash, 
  FaEye, 
  FaPlus, 
  FaTimes, 
  FaThLarge, 
  FaTable,
  FaChevronLeft,
  FaChevronRight,
  FaExclamationTriangle,
  FaNewspaper,
  FaChartLine,
  FaEye as FaEyeIcon,
  FaCalendarAlt
} from "react-icons/fa";
import { useBlogs } from "./BlogContext";
import { useAuth } from "./AuthContext";
import BlogFormModal from "./BlogFormModal";
import "./BlogsPage.css";

function BlogsPage({ addToast }) {
  const { blogs, loading, addBlog, updateBlog, deleteBlog } = useBlogs();
  const { user, canEdit, canDelete, canAdd } = useAuth();
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedBlog, setSelectedBlog] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingBlog, setEditingBlog] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [viewMode, setViewMode] = useState("card");
  const [showPermissionWarning, setShowPermissionWarning] = useState(false);
  
  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);

  // Statistics states
  const [stats, setStats] = useState({
    totalBlogs: 0,
    totalViews: 0,
    averageViews: 0,
    mostViewedBlog: null,
    mostViewedCount: 0,
    blogsThisMonth: 0,
    blogsLastMonth: 0,
    growth: 0
  });

  // Helper function to strip HTML tags and create plain text excerpt
  function stripHtmlAndTruncate(htmlContent, maxLength) {
    if (!htmlContent) return '';
    
    // Create a temporary div to strip HTML tags
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = htmlContent;
    
    // Get text content (this automatically strips all HTML tags)
    let text = tempDiv.textContent || tempDiv.innerText || '';
    
    // Decode HTML entities
    text = text.replace(/&nbsp;/g, ' ')
               .replace(/&amp;/g, '&')
               .replace(/&lt;/g, '<')
               .replace(/&gt;/g, '>')
               .replace(/&quot;/g, '"')
               .replace(/&#39;/g, "'");
    
    // Clean up extra whitespace
    text = text.replace(/\s+/g, ' ').trim();
    
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength) + '...';
  }

  // Calculate statistics from blogs
  useEffect(() => {
    if (blogs.length > 0) {
      calculateStats();
    }
  }, [blogs]);

  const calculateStats = () => {
    const totalBlogs = blogs.length;
    const totalViews = blogs.reduce((sum, blog) => sum + (blog.views || 0), 0);
    const averageViews = totalBlogs > 0 ? Math.round(totalViews / totalBlogs) : 0;
    
    // Find most viewed blog
    let mostViewedBlog = null;
    let mostViewedCount = 0;
    blogs.forEach(blog => {
      if ((blog.views || 0) > mostViewedCount) {
        mostViewedCount = blog.views || 0;
        mostViewedBlog = blog;
      }
    });
    
    // Calculate blogs created this month vs last month
    const now = new Date();
    const currentMonth = now.getMonth();
    const currentYear = now.getFullYear();
    
    const blogsThisMonth = blogs.filter(blog => {
      const blogDate = new Date(blog.createdAt);
      return blogDate.getMonth() === currentMonth && blogDate.getFullYear() === currentYear;
    }).length;
    
    const lastMonth = currentMonth === 0 ? 11 : currentMonth - 1;
    const lastMonthYear = currentMonth === 0 ? currentYear - 1 : currentYear;
    const blogsLastMonth = blogs.filter(blog => {
      const blogDate = new Date(blog.createdAt);
      return blogDate.getMonth() === lastMonth && blogDate.getFullYear() === lastMonthYear;
    }).length;
    
    const growth = blogsLastMonth > 0 
      ? Math.round(((blogsThisMonth - blogsLastMonth) / blogsLastMonth) * 100) 
      : (blogsThisMonth > 0 ? 100 : 0);
    
    setStats({
      totalBlogs,
      totalViews,
      averageViews,
      mostViewedBlog,
      mostViewedCount,
      blogsThisMonth,
      blogsLastMonth,
      growth
    });
  };

  // Check permissions
  const hasDeletePermission = canDelete();
  const hasEditPermission = canEdit();
  const hasAddPermission = canAdd();

  // Filter blogs based on search
  const filteredBlogs = blogs.filter(blog =>
    blog.heading?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Pagination logic
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filteredBlogs.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(filteredBlogs.length / itemsPerPage);

  // Reset to first page when search changes
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm]);

  const paginate = (pageNumber) => setCurrentPage(pageNumber);
  const nextPage = () => setCurrentPage(prev => Math.min(prev + 1, totalPages));
  const prevPage = () => setCurrentPage(prev => Math.max(prev - 1, 1));

  const getPageNumbers = () => {
    const pageNumbers = [];
    const maxPagesToShow = 5;
    
    if (totalPages <= maxPagesToShow) {
      for (let i = 1; i <= totalPages; i++) {
        pageNumbers.push(i);
      }
    } else {
      if (currentPage <= 3) {
        for (let i = 1; i <= 4; i++) {
          pageNumbers.push(i);
        }
        pageNumbers.push('...');
        pageNumbers.push(totalPages);
      } else if (currentPage >= totalPages - 2) {
        pageNumbers.push(1);
        pageNumbers.push('...');
        for (let i = totalPages - 3; i <= totalPages; i++) {
          pageNumbers.push(i);
        }
      } else {
        pageNumbers.push(1);
        pageNumbers.push('...');
        for (let i = currentPage - 1; i <= currentPage + 1; i++) {
          pageNumbers.push(i);
        }
        pageNumbers.push('...');
        pageNumbers.push(totalPages);
      }
    }
    return pageNumbers;
  };

  const handleAddBlog = () => {
    if (!hasAddPermission) {
      addToast('You do not have permission to add blogs', 'error', 3000);
      return;
    }
    setEditingBlog(null);
    setIsModalOpen(true);
  };

  const handleEditBlog = (blog) => {
    if (!hasEditPermission) {
      setShowPermissionWarning(true);
      setTimeout(() => setShowPermissionWarning(false), 3000);
      addToast('Access Denied! Only Admin and Super Admin can edit blogs.', 'error', 3000);
      return;
    }
    setEditingBlog(blog);
    setIsModalOpen(true);
  };

  const handleDeleteClick = (blog) => {
    if (!hasDeletePermission) {
      setShowPermissionWarning(true);
      setTimeout(() => setShowPermissionWarning(false), 3000);
      addToast('Access Denied! Only Super Admin can delete blogs.', 'error', 3000);
      return;
    }
    setDeleteConfirm(blog);
  };

  const handleSubmitBlog = async (blogData) => {
    if (editingBlog) {
      const result = await updateBlog(editingBlog._id, blogData);
      if (result.success) {
        addToast('Blog updated successfully!', 'success', 3000);
      }
      return result;
    } else {
      const result = await addBlog(blogData);
      if (result.success) {
        addToast('Blog added successfully!', 'success', 3000);
      }
      return result;
    }
  };

  const handleDeleteBlog = async () => {
    if (!deleteConfirm) return;
    
    const result = await deleteBlog(deleteConfirm._id);
    if (result.success) {
      addToast(`Blog "${deleteConfirm.heading}" deleted successfully`, 'success', 3000);
      setDeleteConfirm(null);
      // Adjust current page if needed
      const newTotalItems = filteredBlogs.length - 1;
      const newTotalPages = Math.ceil(newTotalItems / itemsPerPage);
      if (currentPage > newTotalPages && newTotalPages > 0) {
        setCurrentPage(newTotalPages);
      }
    } else {
      addToast(result.error || 'Failed to delete blog', 'error', 3000);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString();
  };

  // Get user role display
  const getUserRoleMessage = () => {
    if (user?.role === 'user') {
      return "You have View and Add permissions only. Edit and Delete are disabled.";
    }
    if (user?.role === 'admin') {
      return "You have View, Add, and Edit permissions. Delete is disabled (Super Admin only).";
    }
    return "";
  };

  if (loading) {
    return (
      <div className="blogs-page">
        <div className="loading-container">
          <div className="spinner"></div>
          <p>Loading blogs...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="blogs-page">
      {/* Permission Warning Banner */}
      {showPermissionWarning && (
        <div className="permission-warning">
          <FaExclamationTriangle />
          <span>Access Denied! You don't have permission to perform this action.</span>
          <button onClick={() => setShowPermissionWarning(false)}>×</button>
        </div>
      )}

      {/* Role Info Banner */}
      {user && user.role !== 'super_admin' && (
        <div className={`role-info ${user.role}`}>
          <FaExclamationTriangle />
          <span>{getUserRoleMessage()}</span>
        </div>
      )}

      {/* Statistics Cards */}
      <div className="stats-grid">
        <div className="stat-card-blog">
          <div className="stat-icon-blog">
            <FaNewspaper />
          </div>
          <div className="stat-info-blog">
            <h3>Total Blogs</h3>
            <p className="stat-number">{stats.totalBlogs}</p>
            <span className="stat-label">Published articles</span>
          </div>
        </div>

        <div className="stat-card-blog">
          <div className="stat-icon-blog">
            <FaEyeIcon />
          </div>
          <div className="stat-info-blog">
            <h3>Total Views</h3>
            <p className="stat-number">{stats.totalViews.toLocaleString()}</p>
            <span className="stat-label">All time views</span>
          </div>
        </div>

        <div className="stat-card-blog">
          <div className="stat-icon-blog">
            <FaChartLine />
          </div>
          <div className="stat-info-blog">
            <h3>Avg. Views per Blog</h3>
            <p className="stat-number">{stats.averageViews.toLocaleString()}</p>
            <span className="stat-label">Average engagement</span>
          </div>
        </div>

        <div className="stat-card-blog">
          <div className="stat-icon-blog">
            <FaCalendarAlt />
          </div>
          <div className="stat-info-blog">
            <h3>Growth</h3>
            <p className={`stat-number ${stats.growth >= 0 ? 'positive' : 'negative'}`}>
              {stats.growth >= 0 ? '+' : ''}{stats.growth}%
            </p>
            <span className="stat-label">vs last month</span>
          </div>
        </div>
      </div>

      {/* Most Viewed Blog Card */}
      {stats.mostViewedBlog && (
        <div className="most-viewed-card">
          <div className="most-viewed-header">
            <h3><FaEyeIcon /> Most Popular Blog</h3>
          </div>
          <div className="most-viewed-content">
            <div className="most-viewed-info">
              <h4>{stats.mostViewedBlog.heading}</h4>
              <p>{stripHtmlAndTruncate(stats.mostViewedBlog.content, 150)}</p>
              <div className="most-viewed-meta">
                <span><FaEyeIcon /> {stats.mostViewedCount.toLocaleString()} views</span>
                <span><FaCalendarAlt /> {formatDate(stats.mostViewedBlog.createdAt)}</span>
              </div>
            </div>
            {stats.mostViewedBlog.image && (
              <div className="most-viewed-image">
                <img src={stats.mostViewedBlog.image} alt={stats.mostViewedBlog.heading} />
              </div>
            )}
          </div>
          <div className="most-viewed-footer">
            <button 
              className="view-blog-btn"
              onClick={() => setSelectedBlog(stats.mostViewedBlog)}
            >
              View Full Blog
            </button>
          </div>
        </div>
      )}

      <div className="blogs-header">
        <div>
          <h2>Blog Management</h2>
          <p className="blogs-count">Total: {filteredBlogs.length} blogs</p>
          {user && <p className="user-role-badge">Logged in as: <span className={`role-${user.role}`}>{user.role?.toUpperCase()}</span></p>}
        </div>
        <div className="blogs-actions">
          <div className="search-box">
            <input
              type="text"
              placeholder="Search blogs..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          <div className="view-toggle">
            <button 
              className={`toggle-btn0 ${viewMode === 'card' ? 'active' : ''}`}
              onClick={() => setViewMode('card')}
              title="Card View"
            >
              <FaThLarge />
            </button>
            <button 
              className={`toggle-btn0 ${viewMode === 'table' ? 'active' : ''}`}
              onClick={() => setViewMode('table')}
              title="Table View"
            >
              <FaTable />
            </button>
          </div>
          <button className="add-blog-btn" onClick={handleAddBlog}>
            <FaPlus /> Add Blog
          </button>
        </div>
      </div>

      {filteredBlogs.length === 0 ? (
        <div className="no-blogs">
          <div className="no-blogs-icon">📝</div>
          <h3>No Blogs Found</h3>
          <p>Click "Add Blog" to create your first blog post</p>
        </div>
      ) : viewMode === 'card' ? (
        <>
          <div className="blogs-grid">
            {currentItems.map((blog) => (
              <div key={blog._id} className="blog-card">
                <div className="blog-image">
                  <img 
                    src={blog.image} 
                    alt={blog.heading} 
                    onError={(e) => {
                      e.target.src = 'https://via.placeholder.com/300x200?text=No+Image';
                    }} 
                  />
                </div>
                <div className="blog-content">
                  <h3>{blog.heading}</h3>
                  <p className="blog-excerpt">
                    {stripHtmlAndTruncate(blog.content, 100)}
                  </p>
                  <div className="blog-meta">
                    <span className="blog-date">
                      {formatDate(blog.createdAt)}
                    </span>
                    <span className="blog-views">
                      <FaEyeIcon /> {blog.views || 0} views
                    </span>
                  </div>
                  <div className="blog-actions">
                    <button 
                      className="blog-btn view-btn"
                      onClick={() => setSelectedBlog(blog)}
                    >
                      <FaEye /> View
                    </button>
                    
                    {hasEditPermission && (
                      <button 
                        className="blog-btn edit-btn"
                        onClick={() => handleEditBlog(blog)}
                      >
                        <FaEdit /> Edit
                      </button>
                    )}
                    
                    {hasDeletePermission && (
                      <button 
                        className="blog-btn delete-btn"
                        onClick={() => handleDeleteClick(blog)}
                      >
                        <FaTrash /> Delete
                      </button>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Pagination for Card View */}
          {totalPages > 1 && (
            <div className="pagination-container">
              <div className="pagination-info">
                Showing {indexOfFirstItem + 1} to {Math.min(indexOfLastItem, filteredBlogs.length)} of {filteredBlogs.length} blogs
              </div>
              <div className="pagination-controls">
                <button 
                  onClick={prevPage} 
                  disabled={currentPage === 1}
                  className="pagination-btn"
                >
                  <FaChevronLeft /> Previous
                </button>
                
                <div className="page-numbers">
                  {getPageNumbers().map((number, index) => (
                    number === '...' ? (
                      <span key={index} className="page-dots">...</span>
                    ) : (
                      <button
                        key={index}
                        onClick={() => paginate(number)}
                        className={`page-number ${currentPage === number ? 'active' : ''}`}
                      >
                        {number}
                      </button>
                    )
                  ))}
                </div>
                
                <button 
                  onClick={nextPage} 
                  disabled={currentPage === totalPages}
                  className="pagination-btn"
                >
                  Next <FaChevronRight />
                </button>
              </div>
            </div>
          )}
        </>
      ) : (
        <>
          <div className="table-responsive">
            <table className="blogs-table">
              <thead>
                <tr>
                  <th>Image</th>
                  <th>Heading</th>
                  <th>Content</th>
                  <th>Views</th>
                  <th>Date</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {currentItems.map((blog) => (
                  <tr key={blog._id}>
                    <td className="blog-table-image">
                      <img 
                        src={blog.image} 
                        alt={blog.heading} 
                        onError={(e) => {
                          e.target.src = 'https://via.placeholder.com/50x50?text=No+Image';
                        }} 
                      />
                    </td>
                    <td className="blog-table-heading">
                      <strong>{blog.heading}</strong>
                    </td>
                    <td className="blog-table-content">
                      {stripHtmlAndTruncate(blog.content, 100)}
                    </td>
                    <td className="blog-table-views">
                      <span className="views-badge">{blog.views || 0}</span>
                    </td>
                    <td className="blog-table-date">
                      {formatDate(blog.createdAt)}
                    </td>
                    <td className="blog-table-actions">
                      <button 
                        className="table-action-btn view-btn"
                        onClick={() => setSelectedBlog(blog)}
                        title="View"
                      >
                        <FaEye />
                      </button>
                      
                      {hasEditPermission && (
                        <button 
                          className="table-action-btn edit-btn"
                          onClick={() => handleEditBlog(blog)}
                          title="Edit"
                        >
                          <FaEdit />
                        </button>
                      )}
                      
                      {hasDeletePermission && (
                        <button 
                          className="table-action-btn delete-btn"
                          onClick={() => handleDeleteClick(blog)}
                          title="Delete"
                        >
                          <FaTrash />
                        </button>
                      )}
                    </td>
                   </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination for Table View */}
          {totalPages > 1 && (
            <div className="pagination-container">
              <div className="pagination-info">
                Showing {indexOfFirstItem + 1} to {Math.min(indexOfLastItem, filteredBlogs.length)} of {filteredBlogs.length} blogs
              </div>
              <div className="pagination-controls">
                <button 
                  onClick={prevPage} 
                  disabled={currentPage === 1}
                  className="pagination-btn"
                >
                  <FaChevronLeft /> Previous
                </button>
                
                <div className="page-numbers">
                  {getPageNumbers().map((number, index) => (
                    number === '...' ? (
                      <span key={index} className="page-dots">...</span>
                    ) : (
                      <button
                        key={index}
                        onClick={() => paginate(number)}
                        className={`page-number ${currentPage === number ? 'active' : ''}`}
                      >
                        {number}
                      </button>
                    )
                  ))}
                </div>
                
                <button 
                  onClick={nextPage} 
                  disabled={currentPage === totalPages}
                  className="pagination-btn"
                >
                  Next <FaChevronRight />
                </button>
              </div>
            </div>
          )}
        </>
      )}

      {/* Blog Form Modal */}
      <BlogFormModal
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          setEditingBlog(null);
        }}
        editBlog={editingBlog}
        onSubmit={handleSubmitBlog}
      />

      {/* Blog View Modal */}
      {selectedBlog && (
        <div className="modal-overlay" onClick={() => setSelectedBlog(null)}>
          <div className="blog-view-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{selectedBlog.heading}</h2>
              <button className="close-btn" onClick={() => setSelectedBlog(null)}>×</button>
            </div>
            <div className="blog-view-content">
              <div className="blog-view-image">
                <img 
                  src={selectedBlog.image} 
                  alt={selectedBlog.heading} 
                  onError={(e) => {
                    e.target.src = 'https://via.placeholder.com/600x300?text=No+Image';
                  }} 
                />
              </div>
              <div className="blog-view-text">
                <div dangerouslySetInnerHTML={{ __html: selectedBlog.content }} />
                <div className="blog-view-meta">
                  <p><strong>Created:</strong> {formatDate(selectedBlog.createdAt)}</p>
                  {selectedBlog.updatedAt !== selectedBlog.createdAt && (
                    <p><strong>Updated:</strong> {formatDate(selectedBlog.updatedAt)}</p>
                  )}
                  <p><strong>Total Views:</strong> {selectedBlog.views || 0}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal - Only shown if user has permission */}
      {deleteConfirm && hasDeletePermission && (
        <div className="modal-overlay" onClick={() => setDeleteConfirm(null)}>
          <div className="delete-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header delete-header">
              <h2>Confirm Delete</h2>
              <button className="close-btn" onClick={() => setDeleteConfirm(null)}>×</button>
            </div>
            <div className="modal-body">
              <p>Are you sure you want to delete this blog?</p>
              <div className="delete-preview">
                <strong>Heading:</strong> {deleteConfirm.heading}
                <br />
                <strong>Views:</strong> {deleteConfirm.views || 0}
              </div>
              <div className="delete-actions">
                <button className="cancel-btn" onClick={() => setDeleteConfirm(null)}>
                  Cancel
                </button>
                <button className="confirm-delete-btn" onClick={handleDeleteBlog}>
                  Delete Permanently
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default BlogsPage;